import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { Plus, X, Trash2, Pencil, Eye, Printer, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalBarcodeScanner } from '@/hooks/useGlobalBarcodeScanner';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PackagingData({ user }) {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions(user);
  const [packaging, setPackaging] = useState([]);
  const [filteredPackaging, setFilteredPackaging] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemNameFilter, setItemNameFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [vendorCodeFilter, setVendorCodeFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editPackaging, setEditPackaging] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [colorSearchQuery, setColorSearchQuery] = useState('');
  const [printBarcode, setPrintBarcode] = useState(null);
  const [printSize, setPrintSize] = useState({ width: 50, height: 30 });
  const [formData, setFormData] = useState({
    ln_y_code: 'LN-Y-', vendor_name: '', vendor_code: '', item_name: '', length: '', width: '', height: '', material: '', colors: []
  });
  const [currentColor, setCurrentColor] = useState({ color_code: '', color_name: '', price: '' });
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [vendorDetailsModal, setVendorDetailsModal] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packagingToDelete, setPackagingToDelete] = useState(null);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);

  // Global barcode scanner for Packaging
  const handleGlobalBarcodeScanned = (scannedCode) => {
    const found = packaging.find(pkg => {
      if (pkg.ln_y_code.toLowerCase() === scannedCode.toLowerCase()) return true;
      if (pkg.colors?.some(color => {
        const colorBarcodeCode = `${pkg.ln_y_code}-${color.color_code}`.toLowerCase();
        return colorBarcodeCode === scannedCode.toLowerCase();
      })) return true;
      return false;
    });
    
    if (found) {
      setViewDetails(found);
      const isColorCode = scannedCode.includes('-') && scannedCode.split('-').length > 3;
      if (isColorCode) setColorSearchQuery(scannedCode.split('-').pop());
      else setColorSearchQuery('');
      toast.success(`✅ Packaging found: ${found.ln_y_code}`);
    } else {
      toast.error(`❌ No packaging found with barcode: ${scannedCode}`);
    }
  };

  useGlobalBarcodeScanner(handleGlobalBarcodeScanned);

  useEffect(() => { fetchPackaging(); fetchVendors(); }, []);

  useEffect(() => {
    let filtered = packaging;

    // Filter by LN-Y Code or Color Code (for barcode scanning)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(pkg => {
        // Check main LN-Y code
        if (pkg.ln_y_code.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if search query matches any color barcode (e.g., "LN-Y-00045-003")
        if (pkg.colors?.some(color => {
          const colorBarcodeCode = `${pkg.ln_y_code}-${color.color_code}`.toLowerCase();
          return colorBarcodeCode === query || colorBarcodeCode.includes(query);
        })) {
          return true;
        }
        
        // Check if search query is exact match for main barcode
        if (pkg.ln_y_code.toLowerCase() === query) {
          return true;
        }
        
        return false;
      });
    }

    // Filter by Item Name
    if (itemNameFilter.trim() !== '') {
      const query = itemNameFilter.toLowerCase();
      filtered = filtered.filter(pkg => 
        pkg.item_name.toLowerCase().includes(query)
      );
    }

    // Filter by Material
    if (materialFilter.trim() !== '') {
      const query = materialFilter.toLowerCase();
      filtered = filtered.filter(pkg => 
        pkg.material.toLowerCase().includes(query)
      );
    }

    // Filter by Supplier
    if (supplierFilter.trim() !== '') {
      filtered = filtered.filter(pkg => 
        pkg.vendor_name && pkg.vendor_name.toLowerCase().includes(supplierFilter.toLowerCase())
      );
    }

    // Filter by Vendor Code
    if (vendorCodeFilter.trim() !== '') {
      filtered = filtered.filter(pkg => 
        pkg.vendor_code && pkg.vendor_code.toLowerCase().includes(vendorCodeFilter.toLowerCase())
      );
    }

    setFilteredPackaging(filtered);
  }, [searchQuery, itemNameFilter, materialFilter, supplierFilter, vendorCodeFilter, packaging]);

  const extractNumeric = (code) => {
    return code.replace(/\D/g, '').replace(/^0+/, '');
  };

  const handleBarcodeSearch = (e) => {
    if (e && e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      const query = searchQuery.toLowerCase().trim();
      
      console.log('🔍 Searching packaging for:', query);
      
      // Find packaging by main code or color code
      const found = packaging.find(pkg => {
        // Check exact match for main LN-Y code
        if (pkg.ln_y_code.toLowerCase() === query) {
          return true;
        }
        
        // Check partial match for main LN-Y code
        if (pkg.ln_y_code.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if query matches any color barcode (e.g., "LN-Y-00045-003")
        if (pkg.colors?.some(color => {
          const colorBarcodeCode = `${pkg.ln_y_code}-${color.color_code}`.toLowerCase();
          return colorBarcodeCode === query || colorBarcodeCode.includes(query);
        })) {
          return true;
        }
        
        return false;
      });
      
      if (found) {
        console.log('✅ Found packaging:', found.ln_y_code);
        setViewDetails(found);
        
        // If it's a color code search, highlight that color
        const colorMatch = query.includes('-') && query.split('-').length > 3;
        if (colorMatch) {
          const colorCode = query.split('-').pop();
          setColorSearchQuery(colorCode);
        } else {
          setColorSearchQuery('');
        }
        
        toast.success(`✅ Found: ${found.ln_y_code}`);
      } else {
        console.log('❌ No packaging found for:', query);
        toast.error(`❌ No packaging found: ${query}`);
      }
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  const handleVendorClick = (vendorName) => {
    const vendor = vendors.find(v => v.vendor_name === vendorName);
    if (vendor) {
      setVendorDetailsModal(vendor);
    }
  };

  const fetchPackaging = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/packaging`, { headers: { Authorization: `Bearer ${token}` } });
      setPackaging(response.data);
      setFilteredPackaging(response.data);
    } catch (error) {
      toast.error('Failed to fetch packaging');
    }
  };

  // Auto-refresh every 5 seconds, but pause when user is editing
  const isUserEditing = open || !!editPackaging;
  useAutoRefresh(fetchPackaging, 5, isUserEditing);

  const addColor = () => {
    if (!currentColor.color_code || !currentColor.color_name) {
      toast.error('Please fill color code and color name');
      return;
    }
    
    // Clean color data before adding - convert empty/invalid values to null, with NaN protection
    const priceValue = (currentColor.price && currentColor.price !== '' && currentColor.price !== '0') ? parseFloat(currentColor.price) : null;
    
    const cleanedColor = {
      color_code: currentColor.color_code,
      color_name: currentColor.color_name,
      price: (priceValue !== null && !isNaN(priceValue)) ? priceValue : null
    };
    
    if (editingColorIndex !== null) {
      const updatedColors = [...formData.colors];
      updatedColors[editingColorIndex] = cleanedColor;
      setFormData({ ...formData, colors: updatedColors });
      setEditingColorIndex(null);
    } else {
      setFormData({ ...formData, colors: [...formData.colors, cleanedColor] });
    }
    setCurrentColor({ color_code: '', color_name: '', price: '' });
  };

  const editColor = (index) => {
    const color = formData.colors[index];
    setCurrentColor({ ...color });
    setEditingColorIndex(index);
  };

  const removeColor = (index) => {
    handleRemoveColor(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.colors.length === 0) {
      toast.error('Please add at least one color');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      
      // Clean up color data - convert empty/invalid values to null, with NaN protection
      const cleanedData = {
        ...formData,
        colors: formData.colors.map(color => {
          const priceValue = (color.price && color.price !== '' && color.price !== '0') ? parseFloat(color.price) : null;
          
          return {
            ...color,
            price: (priceValue !== null && !isNaN(priceValue)) ? priceValue : null
          };
        })
      };
      
      if (editPackaging) {
        await axios.put(`${API}/packaging/${editPackaging.id}`, cleanedData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/packaging`, cleanedData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditPackaging(null);
      setFormData({ ln_y_code: 'LN-Y-', vendor_name: '', vendor_code: '', item_name: '', length: '', width: '', height: '', material: '', colors: [] });
      fetchPackaging();
    } catch (error) {
      // Handle Pydantic validation errors and other error types
      let errorMsg = 'Operation failed';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail.msg) {
          errorMsg = detail.msg;
        }
      }
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (pkg) => {
    setPackagingToDelete(pkg);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!packagingToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/packaging/${packagingToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setPackagingToDelete(null);
      fetchPackaging();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPackagingToDelete(null);
  };

  const handleRemoveColor = (index) => {
    setColorToDelete(index);
    setDeleteColorDialogOpen(true);
  };

  const confirmColorDelete = () => {
    if (colorToDelete === null) return;
    const updatedColors = formData.colors.filter((_, i) => i !== colorToDelete);
    setFormData({ ...formData, colors: updatedColors });
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
    toast.success('Color removed');
  };

  const cancelColorDelete = () => {
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

    const handlePrint = () => {
    if (!printBarcode) return;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${printBarcode.code}</title>
          <style>
            @page { 
              size: ${printSize.width}mm ${printSize.height}mm;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
            }
            body { 
              margin: 0; 
              padding: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              width: ${printSize.width}mm;
              height: ${printSize.height}mm;
            }
            .barcode-container { 
              text-align: center;
              page-break-inside: avoid;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img { 
              max-width: 100%;
              max-height: 100%;
              display: block;
            }
          </style>
        </head>
        <body>
          <div class="barcode-container">
            <img src="${printBarcode.image}" alt="Barcode" onload="window.print(); window.onafterprint = function() { window.close(); }" />
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Packaging (LN-Y-CODE)</h1>
              <p className="text-sm text-gray-600">Manage packaging materials</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
        {/* Main Search Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search or scan barcode" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} 
              onKeyDown={handleBarcodeSearch} 
              className="pl-10"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              tabIndex="1"
              data-barcode-input="true"
            />
          </div>
          {canCreate('master_data') && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={async () => { 
                  setEditPackaging(null); 
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API}/master-data/next-code/packaging`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setFormData({ ln_y_code: data.next_code, vendor_name: '', vendor_code: '', item_name: '', length: '', width: '', height: '', material: '', colors: [] });
                    } else {
                      setFormData({ ln_y_code: 'LN-Y-00001', vendor_name: '', vendor_code: '', item_name: '', length: '', width: '', height: '', material: '', colors: [] });
                    }
                  } catch (error) {
                    console.error('Error fetching next code:', error);
                    setFormData({ ln_y_code: 'LN-Y-00001', vendor_name: '', vendor_code: '', item_name: '', length: '', width: '', height: '', material: '', colors: [] });
                  }
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Packaging
                </Button>
              </DialogTrigger>
            </Dialog>
          )}
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Item Name</Label>
            <Input
              placeholder="Search by item name"
              value={itemNameFilter}
              onChange={(e) => setItemNameFilter(e.target.value)}
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Material</Label>
            <Input
              placeholder="Search by material"
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Supplier</Label>
            <SearchableSelect
              options={[
                { value: '', label: 'All Suppliers' },
                ...Array.from(new Set(packaging.map(p => p.vendor_name).filter(Boolean)))
                  .sort()
                  .map(supplier => ({ value: supplier, label: supplier }))
              ]}
              value={supplierFilter}
              onChange={setSupplierFilter}
              placeholder="Select supplier..."
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Vendor Code</Label>
            <Input
              type="text"
              value={vendorCodeFilter}
              onChange={(e) => setVendorCodeFilter(e.target.value)}
              placeholder="Type vendor code..."
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Actions</Label>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setItemNameFilter('');
                setMaterialFilter('');
                setSupplierFilter('');
                setVendorCodeFilter('');
              }}
              className="w-full text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setErrorMessage(''); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-4xl">
            <DialogHeader><DialogTitle>{editPackaging ? 'Edit Packaging' : 'Add New Packaging (LN-Y-CODE)'}</DialogTitle></DialogHeader>
            {errorMessage && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorMessage('')}
                    className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> LN-Y Code</Label>
                <Input 
                  value={formData.ln_y_code} 
                  onChange={(e) => {
                    setFormData({ ...formData, ln_y_code: e.target.value });
                  }}
                  required 
                  placeholder="LN-Y-00001" 
                />
                <p className="text-xs text-gray-500">Auto-generated, but you can edit it</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Vendor</Label>
                  <SearchableSelect
                    options={[
                      { value: "", label: "Select Vendor" },
                      ...vendors.map(v => ({ value: v.vendor_name, label: v.vendor_name }))
                    ]}
                    value={formData.vendor_name}
                    onChange={(value) => setFormData({ ...formData, vendor_name: value })}
                    placeholder="Select Vendor"
                    searchPlaceholder="Search vendor..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor Code</Label>
                  <Input value={formData.vendor_code} onChange={(e) => setFormData({ ...formData, vendor_code: e.target.value })} placeholder="Enter vendor code" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Item Name</Label>
                  <Input value={formData.item_name} onChange={(e) => setFormData({ ...formData, item_name: e.target.value })} required placeholder="Enter item name" />
                </div>
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Material</Label>
                  <Input value={formData.material} onChange={(e) => setFormData({ ...formData, material: e.target.value })} required placeholder="Enter material type" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Length (INCH)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.length} 
                    onChange={(e) => setFormData({ ...formData, length: e.target.value })} 
                    required 
                    placeholder="Enter length in inches" 
                  />
                </div>
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Width (INCH)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.width} 
                    onChange={(e) => setFormData({ ...formData, width: e.target.value })} 
                    required 
                    placeholder="Enter width in inches" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Depth (INCH)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.height} 
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })} 
                    placeholder="Enter depth in inches" 
                  />
                </div>
              </div>
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Add Colors</h3>
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs mb-1">Color Code</Label>
                      <Input value={currentColor.color_code} onChange={(e) => setCurrentColor({ ...currentColor, color_code: e.target.value })} placeholder="Color code" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Color Name</Label>
                      <Input value={currentColor.color_name} onChange={(e) => setCurrentColor({ ...currentColor, color_name: e.target.value })} placeholder="Color name" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Price</Label>
                      <Input type="number" step="0.01" value={currentColor.price} onChange={(e) => setCurrentColor({ ...currentColor, price: parseFloat(e.target.value) })} placeholder="$" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={addColor}>
                      {editingColorIndex !== null ? (
                        <>
                          <Pencil className="w-4 h-4 mr-2" /> Update Color
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" /> Add Color
                        </>
                      )}
                    </Button>
                    {editingColorIndex !== null && (
                      <Button type="button" variant="outline" onClick={() => { setEditingColorIndex(null); setCurrentColor({ color_code: '', color_name: '', price: '' }); }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
                {formData.colors.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {formData.colors.map((color, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div>
                          <span className="font-medium">{color.color_code}</span> - {color.color_name}
                          {color.price && ` ($${color.price} USD)`}
                          {!color.price && <span className="text-gray-400 text-sm ml-2">(No price set)</span>}
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" size="icon" onClick={() => editColor(index)}>
                            <Pencil className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeColor(index)}>
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full">{editPackaging ? 'Update' : 'Create'} Packaging</Button>
            </form>
          </DialogContent>
        </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredPackaging.map((pkg) => (
          <div key={pkg.id} className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setViewDetails(pkg)}>
            <h3 className="text-lg font-bold text-center mb-2">{pkg.ln_y_code}</h3>
            <p className="text-xs text-gray-500 text-center mb-3">{pkg.colors?.length || 0} colors</p>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {canEdit('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => { setEditPackaging(pkg); setFormData(pkg); setOpen(true); }}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDelete('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(pkg)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredPackaging.length === 0 && (
        <div className="text-center py-12"><p className="text-gray-500">No packaging found.</p></div>
      )}

      <Dialog open={!!viewDetails} onOpenChange={() => { setViewDetails(null); setColorSearchQuery(''); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Packaging Details - {viewDetails?.ln_y_code}</DialogTitle></DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">LN-Y Code:</span> {viewDetails.ln_y_code}</div>
                {viewDetails.vendor_name && (
                  <div className="col-span-2 flex items-center gap-2">
                    <span className="font-semibold">Vendor:</span>
                    <span 
                      onClick={() => handleVendorClick(viewDetails.vendor_name)}
                      className="cursor-pointer hover:underline text-blue-600"
                    >
                      {viewDetails.vendor_name}
                    </span>
                    {(() => {
                      const vendor = vendors.find(v => v.vendor_name === viewDetails.vendor_name);
                      return vendor && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          vendor.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {vendor.local_or_imported}-{vendor.country}
                        </span>
                      );
                    })()}
                  </div>
                )}
                {viewDetails.vendor_code && (
                  <div><span className="font-semibold">Vendor Code:</span> {viewDetails.vendor_code}</div>
                )}
                <div><span className="font-semibold">Item Name:</span> {viewDetails.item_name}</div>
                <div>
                  <span className="font-semibold">Size:</span> {
                    viewDetails.length && viewDetails.width && viewDetails.height
                      ? `L: ${viewDetails.length} INCH, W: ${viewDetails.width} INCH, D: ${viewDetails.height} INCH`
                      : viewDetails.size_cm ? `${viewDetails.size_cm} CM` : 'N/A'
                  }
                </div>
                <div><span className="font-semibold">Material:</span> {viewDetails.material}</div>
              </div>
              
              {/* Main Packaging Barcode Section */}
              {viewDetails.barcode_path && (
                <div className="border-t pt-4 pb-4 border-b">
                  <div className="space-y-2 text-center">
                    <Button variant="outline" size="sm" onClick={() => setPrintBarcode({ 
                      image: `${API}/${viewDetails.barcode_path}`, 
                      code: viewDetails.ln_y_code 
                    })}>
                      <Printer className="w-3 h-3 mr-1" /> Print Main Barcode
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Colors ({viewDetails.colors?.filter(color => {
                    if (!colorSearchQuery.trim()) return true;
                    
                    let query = colorSearchQuery.trim();
                    
                    // If it's a full barcode (e.g., "LN-Y-00095-001"), extract just the color part
                    if (query.includes('-') && query.split('-').length >= 4) { // LN-Y-code-color has 4 parts
                      query = query.split('-').pop(); // Get the last part (color code)
                    }
                    
                    // Clean the query and color code for comparison
                    query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                    const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                    
                    // Match by color code, color name, or barcode
                    return colorCode.includes(query) || 
                           color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase()) ||
                           color.color_name.toLowerCase().includes(colorSearchQuery.toLowerCase());
                  }).length || 0})</h4>
                  <div className="relative flex-1 max-w-xs ml-4">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search by code, name, or barcode" 
                      value={colorSearchQuery}
                      onChange={(e) => setColorSearchQuery(e.target.value)}
                      className="pl-10"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      tabIndex="2"
                      data-barcode-input="true"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {viewDetails.colors?.filter(color => {
                    if (!colorSearchQuery.trim()) return true;
                    
                    let query = colorSearchQuery.trim();
                    
                    // If it's a full barcode (e.g., "LN-Y-00095-001"), extract just the color part
                    if (query.includes('-') && query.split('-').length >= 4) { // LN-Y-code-color has 4 parts
                      query = query.split('-').pop(); // Get the last part (color code)
                    }
                    
                    // Clean the query and color code for comparison
                    query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                    const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                    
                    // Match by color code, color name, or barcode
                    return colorCode.includes(query) || 
                           color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase()) ||
                           color.color_name.toLowerCase().includes(colorSearchQuery.toLowerCase());
                  }).map((color, idx) => (
                    <div key={idx} className="border rounded p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div><span className="font-medium">Code:</span> {color.barcode?.code || `${viewDetails.ln_y_code}-${color.color_code}`}</div>
                        <div><span className="font-medium">Name:</span> {color.color_name}</div>
                        <div><span className="font-medium">Price:</span> ${color.price} USD</div>
                      </div>
                      {color.barcode_path && (
                        <div className="space-y-2">
                          <Button variant="outline" size="sm" className="w-full" onClick={() => setPrintBarcode({ 
                            image: `${API}/${color.barcode_path}`, 
                            code: `${viewDetails.ln_y_code}-${color.color_code}` 
                          })}>
                            <Printer className="w-3 h-3 mr-1" /> Print Barcode
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!printBarcode} onOpenChange={() => setPrintBarcode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Print Barcode - {printBarcode?.code}</DialogTitle></DialogHeader>
          {printBarcode && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={printBarcode.image} alt="Barcode" className="max-w-full" />
              </div>
              <div className="space-y-3">
                <Label>Print Size (mm)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width</Label>
                    <Input type="number" value={printSize.width} onChange={(e) => setPrintSize({ ...printSize, width: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-sm">Height</Label>
                    <Input type="number" value={printSize.height} onChange={(e) => setPrintSize({ ...printSize, height: e.target.value })} />
                  </div>
                </div>
              </div>
              <Button onClick={handlePrint} className="w-full">
                <Printer className="w-4 h-4 mr-2" /> Print Barcode
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Details Popup Modal */}
      <Dialog open={!!vendorDetailsModal} onOpenChange={() => setVendorDetailsModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vendor Details - {vendorDetailsModal?.vendor_name}</DialogTitle>
          </DialogHeader>
          {vendorDetailsModal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">Name:</span> {vendorDetailsModal.vendor_name}</div>
                <div>
                  <span className="font-semibold">Type: </span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    vendorDetailsModal.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {vendorDetailsModal.local_or_imported}
                  </span>
                </div>
                <div><span className="font-semibold">Vendor Type:</span> {vendorDetailsModal.vendor_type || 'N/A'}</div>
                <div><span className="font-semibold">Phone:</span> {vendorDetailsModal.phone_number || 'N/A'}</div>
                <div><span className="font-semibold">Alt Number:</span> {vendorDetailsModal.alt_number || 'N/A'}</div>
                <div><span className="font-semibold">City:</span> {vendorDetailsModal.city}</div>
                <div><span className="font-semibold">Country:</span> {vendorDetailsModal.country}</div>
                <div className="col-span-2"><span className="font-semibold">Address:</span> {vendorDetailsModal.address || 'N/A'}</div>
              </div>
              {vendorDetailsModal.wechat_qr_code && (
                <div className="mt-4 pt-4 border-t">
                  <p className="font-semibold mb-2">WeChat QR Code:</p>
                  <img 
                    src={vendorDetailsModal.wechat_qr_code} 
                    alt="WeChat QR Code" 
                    className="w-48 h-48 object-contain border rounded p-2"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Packaging"
        message={packagingToDelete ? `Enter authorization code to delete packaging ${packagingToDelete.ln_y_code}` : 'Enter authorization code to proceed'}
      />

      {/* Delete Color Authorization Dialog */}
      <AuthorizationDialog
        open={deleteColorDialogOpen}
        onOpenChange={setDeleteColorDialogOpen}
        onAuthorized={confirmColorDelete}
        title="Remove Color"
        message={colorToDelete !== null && formData.colors[colorToDelete] ? `Enter authorization code to remove color ${formData.colors[colorToDelete].color_name}` : 'Enter authorization code to proceed'}
      />
      </main>
    </div>
  );
}